import {Edit} from '@mui/icons-material';
import {useCan} from 'src/permissions/hooks';
import {Box, Button, Typography} from '@mui/material';
import {FC, useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {businessesApi} from 'src/api';
import {useAppDispatch, useAppSelector} from 'src/app/hooks';
import {AppDispatch} from 'src/app/store';
import {ColorField} from 'src/components/common/ColorField';
import {DropZoneModal} from 'src/components/common/DropZoneModal';
import {preloaderChangeStatus, updateUserPhoto} from 'src/stores/common/commonSlice';
import {blankPhoto} from 'src/utils/consts';
import {
    buttonImageWrapper,
    buttonImageWrapperSx,
    detailCellSx,
    detailItemSx,
    detailsInnerSx,
    detailsTitleSx,
    detailsWrapperSx,
    imageButton,
    imageButtonSx,
    imageInnerSx,
    imageWrapperSx,
} from './BusinessDetails.sx';
import {BusinessDetailsType} from './BusinessDetails.types';
import {EditIcon} from '../../icons/EditIcon';
import CountUp from 'react-countup';
import AMPModal from '../../common/AMPModal';
import {Link} from 'react-router-dom';
import {BoltOutlined, DescriptionOutlined, LocationOnOutlined, PeopleOutline, PhotoCameraOutlined,} from '@mui/icons-material';
import {actionButtonSx} from 'src/components/common/actionButton.sx';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {RecordActionBar} from 'src/components/mobile/RecordActionBar';
import {actionSlotSx, legacyActionsSx, squareActionSx} from 'src/components/mobile/RecordActionBar/RecordActionBar.sx';
import {RecordIdentityCard} from 'src/components/mobile/RecordIdentityCard';
import {AppRouteEnum, AppRouteSpace, getOcppWsURL} from 'src/utils/helper';
import {
    cardSx,
    counterHeadSx,
    counterIconSx,
    counterLabelSx,
    counterLinkSx,
    counterSx,
    counterValueSx,
    countersSx,
    factEmptySx,
    factLabelSx,
    factSx,
    factValueSx,
    factsSx,
    headerSx,
    logoSx,
    subtitleSx,
    titleSx,
} from './BusinessIdentity.sx';
import {EditBusinessForm} from '../../forms/EditBusinessForm';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {BusinessResetWrapper} from '../BusinessResetWrapper';
import {UserTypeEnum} from 'src/enums/common';

type AddImageMutationTrigger = ReturnType<typeof businessesApi.useAddBusinessLogoMutation>[0];

const handleTrigger = async (
    file: File[],
    businessId: number,
    trigger: AddImageMutationTrigger,
    dispatch: AppDispatch,
    handleCloseImageModal: () => void,
    handleBlockSubmit: (status: boolean) => void
) => {
    try {
        handleBlockSubmit(true);
        dispatch(preloaderChangeStatus(true));
        const formData = new FormData();
        formData.append('file', file[0]);
        const response = await trigger({formData, businessId}).unwrap();
        dispatch(updateUserPhoto(response.logo));
    } catch (error) {
        if (isErrorObject(error)) {
            NotifyError(error.data.message);
        }
    } finally {
        handleCloseImageModal();
        dispatch(preloaderChangeStatus(false));
        handleBlockSubmit(false);
    }
};

export const BusinessDetails: FC<BusinessDetailsType> = ({
                                                             data,
                                                             locations = 0,
                                                             chargers = 0,
                                                             contracts = 0,
                                                             users = 0,
                                                         }) => {
    const {authInfo} = useAppSelector((state) => state.common);

    const intl = useIntl();
    const canUpdateBusiness = useCan('businesses.update');
    const [openDropZone, setOpenDropZone] = useState<boolean>(false);
    const [userImage, setUserImage] = useState<File[] | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);

    const [trigger] = businessesApi.useAddBusinessLogoMutation();
    const [blockSubmit, setBlockSubmit] = useState(false);

    const handleBlockSubmit = (status: boolean) => {
        setBlockSubmit(status);
    };

    const uploadDialogTitle = intl.formatMessage({id: 'addNewImage'});

    const dispatch = useAppDispatch();

    const handleOpenImageModal = () => {
        setOpenDropZone(true);
    };

    const handleCloseImageModal = () => {
        setOpenDropZone(false);
    };

    const handleSetImage = (file: File[] | null) => {
        setUserImage(file);
    };

    const handleSubmit = () => {
        userImage &&
        handleTrigger(
            userImage,
            data.id,
            trigger,
            dispatch,
            handleCloseImageModal,
            handleBlockSubmit
        );
    };

    const handleShowEditModal = (status: boolean) => () => setShowEditModal(status);

    const ocppWsBase = getOcppWsURL();

    const facts = [
        {label: 'address', value: data.address && [
            [data.address.street, data.address.houseNumber].filter(Boolean).join(' '),
            data.address.city,
            data.address.country,
        ].filter(Boolean).join(', ')},
        // The tenant segment of the station's websocket path is this business's uuid, and the station
        // appends its own chargePointId after it - hence the trailing slash. Undefined when the
        // environment names no central-service, which the facts list prints as "not set".
        {label: 'urlOfConnection', value: ocppWsBase && `${ocppWsBase}/${data.uuid}/`},
    ];

    const counters = [
        {
            label: 'locations',
            icon: <LocationOnOutlined/>,
            value: locations,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.locations]),
        },
        {
            label: 'chargers',
            icon: <BoltOutlined/>,
            value: chargers,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.chargers]),
        },
        {
            label: 'contracts',
            icon: <DescriptionOutlined/>,
            value: contracts,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.contracts]),
        },
        {
            label: 'users',
            icon: <PeopleOutline/>,
            value: users,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.users]),
        },
    ];

    const {isCompact} = useScreenTier();

    /** The four counters and the two form fields, as the facts the canvas puts under the name. */
    const phoneFacts = [
        ...counters.map((counter) => ({
            id: counter.label,
            label: <FormattedMessage id={counter.label}/>,
            // Still a link: the tabs below list exactly these rows, which is where the counter led.
            value: <Link to={counter.to}>{counter.value}</Link>,
        })),
        ...facts.map((fact) => ({
            id: fact.label,
            label: <FormattedMessage id={fact.label}/>,
            value: fact.value || <FormattedMessage id="notSet"/>,
        })),
    ];

    return (
        <Box sx={isCompact ? undefined : cardSx}>
            {isCompact && (
                <RecordIdentityCard
                    // The business's own mark, as the badge.
                    icon={(
                        <img
                            src={data?.logo?.url ?? blankPhoto}
                            alt={data?.name}
                        />
                    )}
                    title={data?.name}
                    subtitle={[
                        data?.businessOwnerInfo?.emailAddress,
                        data?.businessOwnerInfo?.phoneNumber,
                    ].filter(Boolean).join(' · ')}
                    facts={phoneFacts}
                />
            )}

            {!isCompact && (
            <Box sx={headerSx}>
                <Box sx={logoSx}>
                    <img
                        src={data?.logo?.url ?? blankPhoto}
                        alt={data?.name}
                    />
                </Box>
                <Box sx={{minWidth: 0}}>
                    <Typography sx={titleSx}>{data?.name}</Typography>
                    {/* Contacts read as how you reach the business, so they sit under its name
                        rather than as two more cells in a form. */}
                    <Typography sx={subtitleSx}>
                        {[
                            data?.businessOwnerInfo?.emailAddress,
                            data?.businessOwnerInfo?.phoneNumber,
                        ].filter(Boolean).join(' · ')}
                    </Typography>
                </Box>

                <Box sx={{flex: '1 1 auto'}}/>

                {/* Both behind businesses.update — the permission both endpoints behind them
                    require. The logo button already asked for it; the edit button asked for nothing
                    at all, though the form it opens saves through the same update call. */}
                {canUpdateBusiness && (
                    <Button
                        variant="contained"
                        sx={actionButtonSx('32px', 'secondary')}
                        startIcon={<PhotoCameraOutlined/>}
                        onClick={handleOpenImageModal}
                    >
                        <FormattedMessage id="logo"/>
                    </Button>
                )}
                {canUpdateBusiness && (
                    <Button
                        variant="contained"
                        sx={actionButtonSx('32px', 'secondary')}
                        startIcon={<Edit/>}
                        onClick={handleShowEditModal(true)}
                    >
                        <FormattedMessage id="edit"/>
                    </Button>
                )}
                <BusinessResetWrapper
                    data={data}
                    toolbar
                />
            </Box>
            )}

            {!isCompact && (
            <>
            <Box sx={countersSx}>
                {counters.map((counter, index) => (
                    <Box
                        key={counter.label}
                        component={Link}
                        to={counter.to}
                        sx={counterSx(index, counters.length)}
                    >
                        <Box sx={counterHeadSx}>
                            <Box sx={counterIconSx}>{counter.icon}</Box>
                            <Typography sx={counterLabelSx}>
                                <FormattedMessage id={counter.label}/>
                            </Typography>
                        </Box>
                        <Typography sx={counterValueSx}>
                            <CountUp
                                start={0}
                                end={counter.value}
                                duration={1}
                            />
                        </Typography>
                        <Typography sx={counterLinkSx}>
                            <FormattedMessage id="openList"/>
                            {' \u203A'}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <Box sx={factsSx}>
                {facts.map((fact, index) => (
                    <Box
                        key={fact.label}
                        sx={factSx(index, facts.length)}
                    >
                        <Typography sx={factLabelSx}>
                            <FormattedMessage id={fact.label}/>
                        </Typography>
                        {fact.value
                            ? <Typography component="div" sx={factValueSx}>{fact.value}</Typography>
                            : <Typography sx={factEmptySx}><FormattedMessage id="notSet"/></Typography>}
                    </Box>
                ))}
            </Box>
            </>
            )}

            {isCompact && (
                <RecordActionBar>
                    {/* The three the header already carried, behind the same gates: the logo and
                        the edit form behind businesses.update, and the password reset that was
                        never gated here and still is not. */}
                    {canUpdateBusiness && (
                        <Box sx={squareActionSx}>
                            <Button
                                variant="contained"
                                sx={actionButtonSx('32px', 'secondary')}
                                startIcon={<PhotoCameraOutlined/>}
                                onClick={handleOpenImageModal}
                                aria-label={intl.formatMessage({id: 'logo'})}
                            />
                        </Box>
                    )}
                    {canUpdateBusiness && (
                        <Box sx={actionSlotSx(true)}>
                            <Button
                                variant="contained"
                                sx={actionButtonSx('32px', 'secondary')}
                                startIcon={<Edit/>}
                                onClick={handleShowEditModal(true)}
                            >
                                <FormattedMessage id="edit"/>
                            </Button>
                        </Box>
                    )}
                    <Box sx={legacyActionsSx}>
                        <BusinessResetWrapper
                            data={data}
                            toolbar
                        />
                    </Box>
                </RecordActionBar>
            )}

            <DropZoneModal
                submitIsBlocked={blockSubmit}
                handleClose={handleCloseImageModal}
                open={openDropZone}
                title={uploadDialogTitle}
                handleFile={handleSetImage}
                handleSubmit={handleSubmit}
                isImage
            />
            <AMPModal
                handleClose={handleShowEditModal(false)}
                open={showEditModal}
                title={intl.formatMessage({id: 'editBusiness'})}
                type="md"
                fullWidth
                withoutPaddings
            >
                <EditBusinessForm
                    businessData={data}
                    handleShowModal={handleShowEditModal}
                />
            </AMPModal>
        </Box>
    );
};
